import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { dropTask } from 'ember-concurrency';
import Button from 'tio-ui/components/button';
import { PhWarningCircle } from 'ember-phosphor-icons';
import { reloadRecord } from 'tio-data/requests/save-record';
import {
  defineCoursesRequest,
  buildTasTransitionFieldData,
} from 'tio-data/requests/tas-application-actions';
import { buildTasEmployeeActor } from 'tio-data/utils/tas-actor';
import { guidedWizardRouteFor } from 'tio-employee/utils/tas/guided-wizard-route';
import type { IntlService } from 'ember-intl';
import type RouterService from '@ember/routing/router-service';
import type { Store as StoreService } from '@warp-drive/core';
import type SessionContextService from 'tio-employee/services/session-context';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TransitionLogSignature } from 'tio-common/types/tuition-assistance';

interface DecisionNoticeSignature {
  Args: {
    application: TasApplicationModel;
  };
  Element: HTMLDivElement;
}

/** Approver "Reject" fires reject_*; "Request Change" fires decline_*. Both land
 * in the states below, and both must be recognised or the notice renders with no
 * reason (the mistake RejectionDetail once made with `not_approve_*`, which was
 * never a real AASM event name). */
const REJECTION_EVENTS = [
  'reject_courses',
  'decline_courses',
  'reject_evidence',
  'decline_evidence',
];

/**
 * Both the approver's hard "Reject" and their "Request Change" land here. The
 * spec draws a single Rejection variant and its copy is used for both, so this
 * set is flat by design — see `title`.
 */
const DECISION_STATES = new Set([
  'TAS.ApplicationState.COURSES_REJECTED',
  'TAS.ApplicationState.COURSES_NOT_APPROVED',
  'TAS.ApplicationState.EVIDENCE_REJECTED',
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
]);

const NS = 'tuition_assistance.applications.manage.decision';

const EVIDENCE_STATES = new Set([
  'TAS.ApplicationState.EVIDENCE_REJECTED',
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
]);

/**
 * "Request Change" outcomes only. `define_courses` accepts exactly these two
 * (plus CREATED) — the hard rejects are terminal in the state machine, so
 * offering resubmission there would 422.
 */
const RESUBMITTABLE_STATES = new Set([
  'TAS.ApplicationState.COURSES_NOT_APPROVED',
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
]);

/**
 * The Figma "Decision Notification — Rejection" card for the manage screen:
 * icon, one-line outcome, the reason beneath it. Nothing else.
 *
 * Self-gating, so the call site does not need to know the states.
 *
 * Deliberately NOT the shared `RejectionDetail` used by the reimagined show
 * page. That component grew a reviewer-note block, a tip banner and a resubmit
 * CTA; the manage screen was wired to it for convenience rather than built from
 * the spec, which is why the two diverged. Scoped here so changing the manage
 * screen does not restyle a page nobody asked about.
 *
 * The spec's Approval variant is already served by the "Needs Your Attention"
 * `approved` anchor, so only the Rejection variant lives here.
 *
 * No dismiss control: the spec offers one, but this notice states an unresolved
 * outcome the employee has to act on, and there is no per-user dismissal store
 * to remember the choice in.
 */
export default class DecisionNotice extends Component<DecisionNoticeSignature> {
  @service declare intl: IntlService;
  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;

  @tracked resubmitError: string | null = null;

  get isRejected(): boolean {
    return DECISION_STATES.has(this.args.application.state);
  }

  get canResubmit(): boolean {
    return RESUBMITTABLE_STATES.has(this.args.application.state);
  }

  /**
   * Reopen the returned application for correction: fire `define_courses`
   * (legal from both request-change states), reload so the state lands at
   * DEFINE_COURSES, then follow the same draft redirect the manage route uses —
   * the wizard is the only surface a DEFINE_COURSES application can be edited
   * on. Closes the dead end where the note said what to change but the page
   * offered no way to act on it (#10286/#10287).
   */
  resubmitTask = dropTask(async () => {
    this.resubmitError = null;
    try {
      const application = this.args.application;
      await this.store.request(
        defineCoursesRequest(
          application.id as string,
          buildTasEmployeeActor(this.sessionContext.user, this.sessionContext.currentRole),
          '',
          buildTasTransitionFieldData(application.fields)
        )
      );
      // The generic transition endpoint returns no resource — reload for the
      // new state before deriving the wizard route from it.
      await reloadRecord(this.store, application);

      const route = guidedWizardRouteFor(application.typeClassification, application.state);
      if (route) {
        this.router.transitionTo(route, application.id as string);
      }
    } catch (e) {
      console.error('Error reopening application for correction:', e);
      this.resubmitError = e instanceof Error ? e.message : String(e);
    }
  });

  get #log(): TransitionLogSignature | undefined {
    const logs = this.args.application.transitionLog ?? [];
    // Newest first: an application can be returned, resubmitted, and returned
    // again, and only the latest decision is the one still standing.
    for (let i = logs.length - 1; i >= 0; i--) {
      const log = logs[i];
      if (log && REJECTION_EVENTS.includes(log.event)) {
        return log;
      }
    }
    return undefined;
  }

  /**
   * The spec's Rejection copy, used for a "Request Change" too. Only the
   * subject varies — the spec's own "[item]" placeholder templates that.
   */
  get title(): string {
    const subject = EVIDENCE_STATES.has(this.args.application.state) ? 'evidence' : 'application';
    return this.intl.t(`${NS}.${subject}_rejected_title`);
  }

  get #reviewerName(): string | undefined {
    return this.#log?.reason?.by?.name;
  }

  get #reviewedOn(): string | undefined {
    const timestamp = this.#log?.timestamp;
    if (!timestamp) {
      return undefined;
    }
    return this.intl.formatDate(new Date(timestamp), {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  /**
   * Who decided, when, and what they said — the useful half of the panel this
   * replaced, on one line instead of three stacked blocks.
   *
   * Every part is optional in the data, so the line is assembled from whatever
   * is present rather than rendered from one template with empty slots: a note
   * with no attribution still says something, and an attribution with no note
   * still tells the employee who to ask.
   */
  get reason(): string {
    const name = this.#reviewerName;
    const date = this.#reviewedOn;
    const note = this.#log?.reason?.note;

    let attribution = '';
    if (name && date) {
      attribution = this.intl.t(`${NS}.reviewed_by_on`, { name, date });
    } else if (name) {
      attribution = this.intl.t(`${NS}.reviewed_by`, { name });
    }

    if (attribution && note) {
      return this.intl.t(`${NS}.attribution_with_note`, { attribution, note });
    }
    return attribution || note || this.intl.t(`${NS}.no_reason`);
  }

  <template>
    {{#if this.isRejected}}
      <div
        class="tio-row-top gap-3 rounded-2xl tio-bg-surface border tio-border-default p-5"
        data-test-decision-notice
        ...attributes
      >
        <PhWarningCircle
          @size={{24}}
          @weight="fill"
          class="shrink-0 tio-fg-danger mt-0.5"
          aria-hidden="true"
        />
        <div class="tio-col gap-1">
          <p class="tio-text-base-semibold tio-fg-primary" data-test-decision-notice-title>
            {{this.title}}
          </p>
          <p class="tio-text-sm-normal tio-fg-secondary" data-test-decision-notice-reason>
            {{this.reason}}
          </p>
          {{#if this.canResubmit}}
            <div class="tio-row items-center gap-3 mt-2">
              <Button
                @intent="primary"
                @appearance="outlined"
                @size="sm"
                @isRunning={{this.resubmitTask.isRunning}}
                @testHook="manage-decision__resubmit"
                {{on "click" this.resubmitTask.perform}}
                data-test-decision-notice-resubmit
              >
                {{t "tuition_assistance.applications.manage.decision.resubmit_cta"}}
              </Button>
              {{#if this.resubmitError}}
                <span
                  class="tio-text-sm-normal tio-fg-danger"
                  data-test-decision-notice-resubmit-error
                >
                  {{t "tuition_assistance.applications.manage.decision.resubmit_error"}}
                </span>
              {{/if}}
            </div>
          {{/if}}
        </div>
      </div>
    {{/if}}
  </template>
}
